
import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatModal } from '@/components/chat/ChatModal';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';

interface ChatButtonProps {
  bookingId?: string;
  groupEventId?: string;
  otherParticipantName?: string;
  currentUserType?: 'client' | 'practitioner';
  className?: string;
  size?: 'sm' | 'default';
  unreadCount?: number;
  onChatOpened?: () => void;
  onRefreshUnreadCounts?: () => void;
  label?: React.ReactNode;
}

export function ChatButton({
  bookingId,
  groupEventId,
  otherParticipantName = 'Other Party',
  currentUserType = 'client',
  className,
  size = 'sm',
  unreadCount: propUnreadCount,
  onChatOpened,
  onRefreshUnreadCounts,
  label
}: ChatButtonProps) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { unreadCounts, markMessagesAsRead, refreshUnreadCounts } = useUnreadMessages({ 
    userId: undefined,
    userType: currentUserType
  });
  const [displayUnreadCount, setDisplayUnreadCount] = useState(0);

  // Only use booking-based unread for bookings; group events don't have per-booking unread tracking yet
  const effectiveUnreadCount = bookingId 
    ? (unreadCounts.chatUnreadByBooking[bookingId] || 0) 
    : (propUnreadCount || 0);

  useEffect(() => {
    setDisplayUnreadCount(effectiveUnreadCount);
  }, [effectiveUnreadCount]);

  // Need either bookingId or groupEventId
  if (!bookingId && !groupEventId) {
    return null;
  }

  const handleChatOpen = async () => {
    setIsChatOpen(true);
    
    if (bookingId && displayUnreadCount > 0) {
      setDisplayUnreadCount(0);
      try {
        await markMessagesAsRead(bookingId, 'chat');
        [200, 1000].forEach(delay => setTimeout(() => refreshUnreadCounts(), delay));
      } catch (error) {
        console.error('ChatButton: Error marking messages as read:', error);
        setDisplayUnreadCount(effectiveUnreadCount);
      }
    }
    
    onChatOpened?.();
  };

  return (
    <>
      <Button
        variant="outline"
        size={size}
        onClick={handleChatOpen}
        className={`relative ${className}`}
      >
        {label || (
          <>
            <MessageCircle className="h-4 w-4 md:mr-1" />
            <span className="hidden md:inline">Chat</span>
          </>
        )}
        {displayUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {displayUnreadCount > 99 ? '99+' : displayUnreadCount}
          </span>
        )}
      </Button>

      <ChatModal
        isOpen={isChatOpen}
        onClose={() => {
          setIsChatOpen(false);
          [100, 500].forEach(delay => {
            setTimeout(() => {
              refreshUnreadCounts();
              onRefreshUnreadCounts?.();
            }, delay);
          });
        }}
        bookingId={bookingId}
        groupEventId={groupEventId}
        otherParticipantName={otherParticipantName}
        currentUserType={currentUserType}
      />
    </>
  );
}
